<template>
  <div
    :class="[
      'carousel-view',
      isFullScreen ? 'full-screen' : '',
    ]"
  >
    <template v-if="mode !== 3">
      <div
        class="bundle"
        :style="{ transform: transformStyle, ...bundleStyle }"
      >
        <div
          :style="getSquareStyle(activeEpisode === 0 ? activeEpisode - 1 : activeEpisode - 2)"
          class="bundle-image-block empty-episode"
          :class="{
            'prev-1': activeEpisode === 0,
            'prev-2': activeEpisode === 1,
          }"
        />
        <div
          :style="getSquareStyle(activeEpisode === 0 ? activeEpisode - 2 : null)"
          class="bundle-image-block empty-episode"
          :class="{
            'prev-2': activeEpisode === 0,
          }"
        />
        <div
          v-for="(image, index) in images"
          :key="index"
          class="bundle-image-block"
          :class="{
            'active': index === activeEpisode,
            'prev-1': index === activeEpisode - 1,
            'prev-2': index === activeEpisode - 2,
            'prev-3': index === activeEpisode - 3,
            'next-1': index === activeEpisode + 1,
            'next-2': index === activeEpisode + 2,
            'next-3': index === activeEpisode + 3,
            'can-hover': getSquareStyle(index).canHover,
            'hovered': hoveredIndex === index,
            'active-episode-hide': getActiveEpisodeHide(index),
            'video-tap-mode': isVideoMode
          }"
          :style="getSquareStyle(index)"
          @mouseenter="handleMouseEnterOnBundleImageBlock(index)"
          @mouseleave="handleMouseLeaveOnBundleImageBlock"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div
            v-if="isModeChanging && index === activeEpisode"
            class="comic-image is-mode-changing"
          >
            <AppLoader
              :size="50"
            />
          </div>
          <img
            :src="getImageSrc(index)"
            class="comic-image"
          >
          <template v-if="isVideoMode">
            <video
              v-show="currentPlayingIndex === index"
              :ref="`video-${index}`"
              class="comic-video"
              :src="getVideoSrc(index)"
              playsinline
              @ended="handleVideoEnded(index)"
            />
          </template>
          <div
            class="comic-image-overlay"
            :class="{
              active: activeEpisode != index,
            }"
            @click="handleClickUnActiveEpisode(index)"
          />
        </div>
        <div
          :style="getSquareStyle(activeEpisode === maxImages - 1 ? activeEpisode + 1 : activeEpisode + 2)"
          class="bundle-image-block empty-episode"
          :class="{
            'next-1': activeEpisode === maxImages - 1,
            'next-2': activeEpisode === maxImages - 2,
          }"
        />
        <div
          :style="getSquareStyle(activeEpisode === maxImages - 1 ? activeEpisode + 2 : null)"
          class="bundle-image-block empty-episode"
          :class="{
            'next-2': activeEpisode === maxImages - 1,
          }"
        />
      </div>
      <div class="comic-footer-wrap">
        <h4 class="episode-counter flex justify-center">
          <template v-if="isFullScreen || breakpoints.xsOnly">
            <ComicsComponentIcon
              v-if="activeEpisode > 0"
              name="comics_arrow_left_icon"
              class="thumb-arrow-short"
              @click="prevEpisode"
            />
            <ComicsComponentIcon
              v-else
              is-disabled
              name="comics_arrow_left_icon"
              class="thumb-arrow-short"
            />
          </template>
          <span class="mr-4 ml-4 font-rubik">
            {{ activeEpisode + 1 }}/{{ maxImages }}
          </span>
          <div v-if="mode === 2"
            class="video-buttons"
            :style="{
              left: `calc(50% - 120px + ${squareStyle.width}/2)`
            }"
          >
            <ComicsComponentIcon
              v-if="isPlayAll"
              name="bolt_icon"
              @click="togglePlayAll"
            />
            <ComicsComponentIcon
              v-else
              name="bolt_off_icon"
              @click="togglePlayAll"
            />
            <ComicsComponentIcon
              v-if="isEnded"
              name="play_skip_icon"
              @click="handlePlayNext"
            />
            <ComicsComponentIcon
              v-else-if="isVideoPaused"
              name="play_icon"
              @click="handlePlayOrPause(activeEpisode)"
            />
            <ComicsComponentIcon
              v-else
              name="pause_icon"
              @click="handlePlayOrPause(activeEpisode)"
            />
            <ComicsComponentIcon
              name="restart_icon"
              @click="handleStop(activeEpisode)"
            />
            <ComicsComponentIcon
              v-if="!isMuted"
              name="speaker_icon"
              @click="handleToggleMute"
            />
            <ComicsComponentIcon
              v-else
              name="speaker_off_icon"
              @click="handleToggleMute"
            />
          </div>
          <template v-if="isFullScreen || breakpoints.xsOnly">
            <ComicsComponentIcon
              v-if="activeEpisode < maxImages - 1"
              name="comics_arrow_right_icon"
              class="thumb-arrow-short"
              @click="nextEpisode"
            />
            <ComicsComponentIcon
              v-else
              is-disabled
              name="comics_arrow_right_icon"
              class="thumb-arrow-short"
            />
          </template>
        </h4>
        <div class="thumbnail-list">
          <ComicsComponentIcon
            v-if="activeEpisode > 0"
            id="arrow-left"
            name="comics_arrow_left_icon"
            class="thumb-arrow"
            @click="prevEpisode"
          />
          <ComicsComponentIcon
            v-else
            id="arrow-left"
            is-disabled
            name="comics_arrow_left_icon"
            class="thumb-arrow"
          />
          <div v-if="!isPrevSeasonActive"
            class="thumbnail empty"
            :class="{
              'prev-1': activeEpisode === 0,
              'prev-2': activeEpisode === 1,
              'prev-3': activeEpisode === 2,
              'prev-4': activeEpisode === 3,
            }"
          />
          <div v-if="!isPrevSeasonActive"
            class="thumbnail empty"
            :class="{
              'prev-2': activeEpisode === 0,
              'prev-3': activeEpisode === 1,
              'prev-4': activeEpisode === 2,
              'is-prev-season-active': isPrevSeasonActive
            }"
          />
          <div v-if="!isPrevSeasonActive"
            class="thumbnail empty"
            :class="{
              'prev-3': activeEpisode === 0,
              'prev-4': activeEpisode === 1,
            }"
          />
          <div v-if="!isPrevSeasonActive"
            class="thumbnail empty"
            :class="{
              'prev-4': activeEpisode === 0,
            }"
          />
          <button v-if="isPrevSeasonActive"
            @click="goToPrevSeason()"
            class="border-button"
            :class="{
              'prev-1': activeEpisode === 0,
              'prev-2': activeEpisode === 1,
              'prev-3': activeEpisode === 2,
              'prev-4': activeEpisode === 3,
              'prev-5': activeEpisode === 4,
            }"
          >
            PREV {{ (breakpoints.xsOnly ? 'S' : 'SEASON ') + (season - 1) }}
          </button>
          <div
            v-for="(image, index) in images"
            :key="'thumb-' + index"
            class="thumbnail"
            :class="{
              'selected': index === activeEpisode,
              'prev-1': index === activeEpisode - 1,
              'prev-2': index === activeEpisode - 2,
              'prev-3': index === activeEpisode - 3,
              'prev-4': index === activeEpisode - 4,
              'next-1': index === activeEpisode + 1,
              'next-2': index === activeEpisode + 2,
              'next-3': index === activeEpisode + 3,
              'next-4': index === activeEpisode + 4,
              'next-5': index === activeEpisode + 5,
            }"
            @click="setActiveEpisode(index)"
          >
            <img
              :src="image.finalImage"
              class="thumbnail-image"
            />
          </div>
          <div v-if="!isNextSeasonActive"
            class="thumbnail empty"
            :class="{
              'next-1': activeEpisode === maxImages - 1,
              'next-2': activeEpisode === maxImages - 2,
              'next-3': activeEpisode === maxImages - 3,
              'next-4': activeEpisode === maxImages - 4,
            }"
          >
          </div>
          <div v-if="!isNextSeasonActive"
            class="thumbnail empty"
            :class="{
              'next-2': activeEpisode === maxImages - 1,
              'next-3': activeEpisode === maxImages - 2,
              'next-4': activeEpisode === maxImages - 3,
            }"
          />
          <div v-if="!isNextSeasonActive"
            class="thumbnail empty"
            :class="{
              'next-3': activeEpisode === maxImages - 1,
              'next-4': activeEpisode === maxImages - 2,
            }"
          />
          <div v-if="!isNextSeasonActive"
            class="thumbnail empty"
            :class="{
              'next-4': activeEpisode === maxImages - 1,
            }"
          />
          <button v-if="isNextSeasonActive"
            @click="this.$emit('setSeason', season + 1)"
            class="border-button"
            :class="{
              'next-1': activeEpisode === maxImages - 1,
              'next-2': activeEpisode === maxImages - 2,
              'next-3': activeEpisode === maxImages - 3,
              'next-4': activeEpisode === maxImages - 4,
              'next-5': activeEpisode === maxImages - 5,
            }"
          >
            NEXT {{ (breakpoints.xsOnly ? 'S' : 'SEASON ') + (season + 1) }}
          </button>
          <ComicsComponentIcon
            v-if="activeEpisode < maxImages - 1"
            id="arrow-right"
            name="comics_arrow_right_icon"
            class="thumb-arrow"
            @click="nextEpisode"
          />
          <ComicsComponentIcon
            v-else
            id="arrow-right"
            is-disabled
            name="comics_arrow_right_icon"
            class="thumb-arrow"
          />
        </div>
      </div>
    </template>
    <div v-else>
      <video
        ref="fullVideo"
        class="comic-full-video"
        :src="getFullVideoSrc()"
        playsinline
        muted
        controls
        :style="getSquareStyle(activeEpisode)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'ComicsComponentCarousel',
  props: {
    isFullScreen: {
      type: Boolean,
    },
    season: {
      type: Number,
      default: 1
    },
    mode: {
      type: Number,
      default: 1
    }
  },
  data: () => ({
    images: [],
    bundles: [],
    bundleSize: 4,
    currentBundleIndex: 0,
    activeEpisode: 0,
    squareStyle: {},
    squareStyle2: {},
    squareStyle3: {},
    bundleStyle: {},
    transformStyle: '',
    squareSize: 0,
    squareGap: 150,
    thumbnailGap: 10,
    thumbnailListStyle: {},
    canPressKey: true,
    hoveredIndex: null,
    isAlwaysOpenActive: false,
    thumbnailSize: 0,
    activeInterval: null,
    startTouchX: null,
    endTouchX: null,
    isTouching: false,
    isZoomed: false,
    breakpoints: useBreakpoints(),
    currentPlayingIndex: null,
    isVideoPaused: true,
    isMuted: false,
    isEnded: false,
    autoPlayTimeoutId: null,
    isModeChanging: false,
    isPlayAll: false
  }),
  computed: {
    squareInitSizeInPercent() {
      return this.breakpoints.xsOnly ? 95 : this.isFullScreen ? 92 : 83
    },
    offsetHeader() {
      return this.breakpoints.xsOnly ? 150 : this.isFullScreen ? 32 : 110
    },
    maxImages() {
      return this.season === 1 ? MAX_IMAGES_SEASON_1 : MAX_IMAGES_SEASON_2
    },
    isNextSeasonActive () {
      return this.season < MAX_SEASON
    },
    isPrevSeasonActive () {
      return this.season > 1
    },
    isVideoMode () {
      return this.mode === 2
    },
    isPlayingCurrent () {
      return this.currentPlayingIndex !== null
    }
  },
  watch: {
    activeEpisode() {
      this.isEnded = false

      if (this.autoPlayTimeoutId !== null) {
        clearTimeout(this.autoPlayTimeoutId)
        this.autoPlayTimeoutId = null
      }

      if (this.hoveredIndex != null) {
        this.isAlwaysOpenActive = true
      }
      this.updateOffsetOfThumbnails()

      if (this.currentPlayingIndex !== null) {
        const videoEl = this.getVideoElByIndex(this.currentPlayingIndex)
        if (videoEl && videoEl.pause) {
          videoEl.pause()
          videoEl.currentTime = 0
        }
        this.currentPlayingIndex = null
        this.isVideoPaused = false
      }

      if (this.isVideoMode) {
        this.scheduleAutoPlay()
      }
    },
    isFullScreen() {
      this.updateSize()
    },
    hoveredIndex(newValue, oldValue) {
      if (newValue === null) {
        if (oldValue !== this.activeEpisode) {
          this.isAlwaysOpenActive = false
        }
      }
    },
    season() {
      this.activeEpisode = 0
      this.generateImages()
      this.isEnded = false

      if (this.isVideoMode) {
        this.scheduleAutoPlay()
      }
    },
    mode(newVal) {
      this.isModeChanging = true
      this.generateImages()
      this.updateOffsetOfThumbnails()

      if (this.autoPlayTimeoutId !== null) {
        clearTimeout(this.autoPlayTimeoutId)
        this.autoPlayTimeoutId = null
      }

      if (!this.isVideoMode && this.currentPlayingIndex !== null) {
        const prevVideo = this.getVideoElByIndex(this.currentPlayingIndex)
        if (prevVideo) {
          prevVideo.pause()
          prevVideo.currentTime = 0
        }
        this.currentPlayingIndex = null
        this.isVideoPaused = true
      }

      this.isEnded = false
      if (this.isVideoMode) {
        this.scheduleAutoPlay()
      }
      setTimeout(() => {
        this.isModeChanging = false
      }, 1000)
    }
  },
  mounted() {
    this.generateImages()
    this.updateSize()
    window.addEventListener('resize', this.updateSize)
    window.addEventListener('keydown', this.handleKeyDown)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.checkVisualViewport)
      this.checkVisualViewport()
    }

    if (this.isVideoMode) {
      this.scheduleAutoPlay()
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateSize)
    window.removeEventListener('keydown', this.handleKeyDown)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.checkVisualViewport);
    }
    if (this.activeInterval !== null) {
      clearInterval(this.activeInterval)
      this.activeInterval = null
    }
    if (this.autoPlayTimeoutId !== null) {
      clearTimeout(this.autoPlayTimeoutId)
      this.autoPlayTimeoutId = null
    }
  },
  methods: {
    togglePlayAll () {
      this.isPlayAll = !this.isPlayAll
    },
    scheduleAutoPlay() {
      if (!this.isVideoMode) return

      if (this.autoPlayTimeoutId !== null) {
        clearTimeout(this.autoPlayTimeoutId)
        this.autoPlayTimeoutId = null
      }

      this.autoPlayTimeoutId = setTimeout(() => {
        this.autoPlayTimeoutId = null
        this.$nextTick(() => {
          this.autoPlayCurrent()
        })
      }, 500)
    },
    checkVisualViewport() {
      if (window.visualViewport) {
        this.isZoomed = window.visualViewport.scale > 1
      }
      else {
        this.isZoomed = false
      }
    },
    generateImages() {
      this.images = []

      const { cdnBaseUrl } = useRuntimeConfig().public
      if (this.isVideoMode) {
        const posterDir = 'video-posters'
        const imageDir = 'images'

        for (let i = 1; i <= this.maxImages; i += 1) {
          this.images.push({
            image: `${cdnBaseUrl}/${posterDir}/season-${this.season}/${i}.webp`,
            finalImage: `${cdnBaseUrl}/${imageDir}/season-${this.season}/${i}.webp`
          })
        }
      }
      else {
        const baseDir = 'images'

        for (let i = 1; i <= this.maxImages; i += 1) {
          this.images.push({
            image: `${cdnBaseUrl}/${baseDir}/season-${this.season}/${i}.webp`,
          })
        }
      }
    },
    nextEpisode() {
      if (!this.canPressKey || this.isZoomed) return
      this.isEnded = false
      clearInterval(this.activeInterval)
      this.activeEpisode = Math.min((this.maxImages - 1), (this.activeEpisode + 1))
      this.toggleKeyAccess()
    },
    prevEpisode() {
      if (!this.canPressKey || this.isZoomed) return
      this.isEnded = false
      clearInterval(this.activeInterval)
      this.activeEpisode = Math.max(0, (this.activeEpisode - 1))
      this.toggleKeyAccess()
    },
    updateSize() {
      const vh = window.innerHeight * 0.01;
      const vw = window.innerWidth * 0.01;

      this.squareSize = Math.min(vh, vw) * this.squareInitSizeInPercent;
      this.squareSize = Math.max(this.squareSize, 280);
      this.squareStyle = {
        width: `calc(${this.squareSize}px - ${this.breakpoints.xsOnly ? 0 : this.offsetHeader}px)`,
        height: `calc(${this.squareSize}px - ${this.breakpoints.xsOnly ? 0 : this.offsetHeader}px)`,
        top: '0px',
      }
      this.squareStyle2 = {
        width: `calc(${this.squareSize * 0.8}px - ${this.breakpoints.xsOnly ? 0 : this.offsetHeader}px)`,
        height: `calc(${this.squareSize * 0.8}px - ${this.breakpoints.xsOnly ? 0 : this.offsetHeader}px)`,
        top: `calc(${this.squareSize * 0.1}px)`,
      }
      this.squareStyle3 = {
        width: `calc(${this.squareSize * 0.6}px - ${this.breakpoints.xsOnly ? 0 : this.offsetHeader}px)`,
        height: `calc(${this.squareSize * 0.6}px - ${this.breakpoints.xsOnly ? 0 : this.offsetHeader}px)`,
        top: `calc(${this.squareSize * 0.2}px)`,
      }
      this.bundleStyle = {
        height: `calc(${this.squareSize}px - ${this.breakpoints.xsOnly ? 0 : this.offsetHeader}px)`,
      }
      this.updateOffsetOfThumbnails();
    },
    getSquareStyle(index) {
      let baseStyle = {
        ...this.squareStyle,
      };

      if (index === this.activeEpisode - 2 || index === this.activeEpisode + 2) {
        baseStyle = {
          ...baseStyle, ...this.squareStyle3,
        };
      }
      else if (index === this.activeEpisode - 1 || index === this.activeEpisode + 1) {
        baseStyle = {
          ...baseStyle, ...this.squareStyle2,
        };
      }
      baseStyle.canHover = this.canPressKey;
      return baseStyle;
    },
    handleClickUnActiveEpisode(clickedIndex) {
      if (this.activeEpisode + 1 === clickedIndex) {
        this.nextEpisode();
      }
      else if (this.activeEpisode - 1 === clickedIndex) {
        this.prevEpisode();
      }
    },
    handleKeyDown(event) {
      switch (event.key) {
      case 'ArrowRight':
        this.nextEpisode()
          break
      case 'ArrowLeft':
        this.prevEpisode()
          break
      }
    },
    handleTouchStart(event) {
      if (event.touches.length === 1) {
        this.isTouching = true;
        this.startTouchX = event.touches[0].clientX;
      }
      else {
        this.isTouching = false;
        this.startTouchX = null;
      }
    },
    handleTouchMove(event) {
      if (this.isTouching && event.touches.length === 1) {
        this.endTouchX = event.touches[0].clientX;
      }
    },
    handleTouchEnd(event) {
      if (this.isTouching) {
        if (event.changedTouches.length === 1) {
          this.endTouchX = event.changedTouches[0].clientX
          const deltaX = this.startTouchX - this.endTouchX
          if (deltaX > 50) {
            this.nextEpisode()
          }
          else if (deltaX < -50) {
            this.prevEpisode()
          }
        }
      }
      this.isTouching = false
      this.startTouchX = null
      this.endTouchX = null
    },
    toggleKeyAccess() {
      this.canPressKey = false
      setTimeout(() => {
        this.canPressKey = true
      }, 500)
    },
    handleMouseEnterOnBundleImageBlock(index) {
      if (index === this.activeEpisode + 1 || index === this.activeEpisode - 1) {
        this.hoveredIndex = index;
      }
    },
    handleMouseLeaveOnBundleImageBlock() {
      this.hoveredIndex = null;
    },
    getActiveEpisodeHide(index) {
      if (this.breakpoints.xsOnly) {
        return false;
      }
      if (this.isAlwaysOpenActive) {
        return false;
      }
      return this.hoveredIndex
        && index === this.activeEpisode
        && (this.hoveredIndex !== this.activeEpisode + 1 || this.hoveredIndex !== this.activeEpisode - 1);
    },
    goToPrevSeason () {
      this.$emit('setSeason', this.season - 1)
      this.$nextTick(() => {
        this.updateOffsetOfThumbnails()
      })
    },
    updateOffsetOfThumbnails() {
      this.$nextTick(() => {

        const allThumbs = this.$el.querySelectorAll('.thumbnail')
        allThumbs.forEach(el => {
          el.style.left = ''
          el.style.top = ''
          el.style.width = ''
          el.style.height = ''
        })

        const activeThumbnail = this.$el.querySelector('.thumbnail.selected');
        if (this.thumbnailSize === 0 && activeThumbnail) {
          this.thumbnailSize = 64;
        }
        const thumbGap = this.thumbnailSize * 0.18;
        const thumbSelected = this.$el.querySelector('.thumbnail.selected');
        if (thumbSelected) {
          thumbSelected.style.left = '50%';
          thumbSelected.style.width = `${this.thumbnailSize}px`;
          thumbSelected.style.height = `${this.thumbnailSize}px`;
          thumbSelected.style.top = `calc(50% - ${(this.thumbnailSize) / 2}px)`;
        }

        if (this.breakpoints.xsOnly) {
          const thumbNext1 = this.$el.querySelector('.thumbnail.next-1');
          if (thumbNext1) {
            thumbNext1.style.left = `calc(50% + ${this.thumbnailSize}px + ${thumbGap}px - ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px)`;
            thumbNext1.style.width = `${this.thumbnailSize * 0.75}px`;
            thumbNext1.style.height = `${this.thumbnailSize * 0.75}px`;
            thumbNext1.style.top = `calc(50% - ${(this.thumbnailSize * 0.75) / 2}px)`;
          }
          const thumbNext2 = this.$el.querySelector('.thumbnail.next-2');
          if (thumbNext2) {
            thumbNext2.style.left = `calc(50% + ${this.thumbnailSize}px + ${thumbGap}px - ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px + ${this.thumbnailSize * 0.75}px)`;
            thumbNext2.style.width = `${this.thumbnailSize * 0.5}px`;
            thumbNext2.style.height = `${this.thumbnailSize * 0.5}px`;
            thumbNext2.style.top = `calc(50% - ${(this.thumbnailSize * 0.5) / 2}px)`;
          }
          const thumbNext3 = this.$el.querySelector('.thumbnail.next-3');
          if (thumbNext3) {
            thumbNext3.style.left = `calc(50% + ${this.thumbnailSize}px + ${thumbGap}px - ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px + ${this.thumbnailSize * 0.75}px + ${this.thumbnailSize * 0.5}px - ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px)`;
            thumbNext3.style.width = `${this.thumbnailSize * 0.5}px`;
            thumbNext3.style.height = `${this.thumbnailSize * 0.5}px`;
            thumbNext3.style.top = `calc(50% - ${(this.thumbnailSize * 0.5) / 2}px)`;
          }

          const thumbPrev1 = this.$el.querySelector('.thumbnail.prev-1');
          if (thumbPrev1) {
            thumbPrev1.style.left = `calc(50% - ${this.thumbnailSize}px - ${thumbGap}px + ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px)`;
            thumbPrev1.style.width = `${this.thumbnailSize * 0.75}px`;
            thumbPrev1.style.height = `${this.thumbnailSize * 0.75}px`;
            thumbPrev1.style.top = `calc(50% - ${(this.thumbnailSize * 0.75) / 2}px)`;
          }
          const thumbPrev2 = this.$el.querySelector('.thumbnail.prev-2');
          if (thumbPrev2) {
            thumbPrev2.style.left = `calc(50% - ${this.thumbnailSize}px - ${thumbGap}px + ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px - ${this.thumbnailSize * 0.75}px)`;
            thumbPrev2.style.width = `${this.thumbnailSize * 0.5}px`;
            thumbPrev2.style.height = `${this.thumbnailSize * 0.5}px`;
            thumbPrev2.style.top = `calc(50% - ${(this.thumbnailSize * 0.5) / 2}px)`;
          }
          const thumbPrev3 = this.$el.querySelector('.thumbnail.prev-3');
          if (thumbPrev3) {
            thumbPrev3.style.left = `calc(50% - ${this.thumbnailSize}px - ${thumbGap}px - ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px - ${this.thumbnailSize * 0.75}px - ${this.thumbnailSize * 0.5}px - ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px)`;
            thumbPrev3.style.width = `${this.thumbnailSize * 0.5}px`;
            thumbPrev3.style.height = `${this.thumbnailSize * 0.5}px`;
            thumbPrev3.style.top = `calc(50% - ${(this.thumbnailSize * 0.5) / 2}px)`;
          }
        }
        else {
          const thumbNext1 = this.$el.querySelector('.thumbnail.next-1');
          if (thumbNext1) {
            thumbNext1.style.left = `calc(50% + ${this.thumbnailSize}px + ${thumbGap}px)`;
            thumbNext1.style.width = `${this.thumbnailSize}px`;
            thumbNext1.style.height = `${this.thumbnailSize}px`;
            thumbNext1.style.top = `calc(50% - ${(this.thumbnailSize) / 2}px)`;
          }

          const thumbNext2 = this.$el.querySelector('.thumbnail.next-2');
          if (thumbNext2) {
            thumbNext2.style.left = `calc(50% + 2*(${this.thumbnailSize}px + ${thumbGap}px))`;
            thumbNext2.style.width = `${this.thumbnailSize}px`;
            thumbNext2.style.height = `${this.thumbnailSize}px`;
            thumbNext2.style.top = `calc(50% - ${(this.thumbnailSize) / 2}px)`;
          }

          const thumbNext3 = this.$el.querySelector('.thumbnail.next-3');
          if (thumbNext3) {
            thumbNext3.style.width = `${this.thumbnailSize * 0.75}px`;
            thumbNext3.style.height = `${this.thumbnailSize * 0.75}px`;
            thumbNext3.style.left = `calc(50% + 3*(${this.thumbnailSize}px + ${thumbGap}px) - ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px)`;
            thumbNext3.style.top = `calc(50% - ${(this.thumbnailSize * 0.75) / 2}px)`;
          }

          const thumbNext4 = this.$el.querySelector('.thumbnail.next-4');
          if (thumbNext4) {
            thumbNext4.style.width = `${this.thumbnailSize * 0.5}px`;
            thumbNext4.style.height = `${this.thumbnailSize * 0.5}px`;
            thumbNext4.style.left = `calc(
              +50%
              + 3*(${this.thumbnailSize}px + ${thumbGap}px)
              + ${this.thumbnailSize * 0.75}px
              + ${thumbGap}px
              - ${(this.thumbnailSize - this.thumbnailSize * 0.5) / 2}px
            )`;
            thumbNext4.style.top = `calc(50% - ${(this.thumbnailSize * 0.5) / 2}px)`;
          }

          const thumbNext5 = this.$el.querySelector('.thumbnail.next-5');
          if (thumbNext5) {
            thumbNext5.style.width = `${this.thumbnailSize * 0.5}px`;
            thumbNext5.style.height = `${this.thumbnailSize * 0.5}px`;
            thumbNext5.style.left = `calc(
              +50%
              + 3*(${this.thumbnailSize}px + ${thumbGap}px)
              + ${this.thumbnailSize * 0.75}px
              + ${thumbGap}px
              - ${(this.thumbnailSize - this.thumbnailSize * 0.5) / 2}px
              + ${this.thumbnailSize * 0.5}px
              + ${thumbGap}px
            )`;
            thumbNext5.style.top = `calc(50% - ${(this.thumbnailSize * 0.5) / 2}px)`;
          }

          const thumbPrev1 = this.$el.querySelector('.thumbnail.prev-1');
          if (thumbPrev1) {
            thumbPrev1.style.left = `calc(50% - ${this.thumbnailSize}px - ${thumbGap}px)`;
            thumbPrev1.style.width = `${this.thumbnailSize}px`;
            thumbPrev1.style.height = `${this.thumbnailSize}px`;
            thumbPrev1.style.top = `calc(50% - ${(this.thumbnailSize) / 2}px)`;
          }

          const thumbPrev2 = this.$el.querySelector('.thumbnail.prev-2');
          if (thumbPrev2) {
            thumbPrev2.style.left = `calc(50% - 2*(${this.thumbnailSize}px + ${thumbGap}px))`;
            thumbPrev2.style.width = `${this.thumbnailSize}px`;
            thumbPrev2.style.height = `${this.thumbnailSize}px`;
            thumbPrev2.style.top = `calc(50% - ${(this.thumbnailSize) / 2}px)`;
          }

          const thumbPrev3 = this.$el.querySelector('.thumbnail.prev-3');
          if (thumbPrev3) {
            thumbPrev3.style.width = `${this.thumbnailSize * 0.75}px`;
            thumbPrev3.style.height = `${this.thumbnailSize * 0.75}px`;
            thumbPrev3.style.left = `calc(50% - 3*(${this.thumbnailSize}px + ${thumbGap}px) + ${(this.thumbnailSize - this.thumbnailSize * 0.75) / 2}px)`;
            thumbPrev3.style.top = `calc(50% - ${(this.thumbnailSize * 0.75) / 2}px)`;
          }

          const thumbPrev4 = this.$el.querySelector('.thumbnail.prev-4');
          if (thumbPrev4) {
            thumbPrev4.style.width = `${this.thumbnailSize * 0.5}px`;
            thumbPrev4.style.height = `${this.thumbnailSize * 0.5}px`;
            thumbPrev4.style.left = `calc(
              +50%
              - 3*(${this.thumbnailSize}px + ${thumbGap}px)
              - ${this.thumbnailSize * 0.75}px
              - ${thumbGap}px
              + ${(this.thumbnailSize - this.thumbnailSize * 0.5) / 2}px
            )`;
            thumbPrev4.style.top = `calc(50% - ${(this.thumbnailSize * 0.5) / 2}px)`;
          }
        }

        const arrowLeftEl = this.$el.querySelector('#arrow-left');
        if (arrowLeftEl) {
          arrowLeftEl.style.left = `calc(
            +50%
            - 3*(${this.thumbnailSize}px + ${thumbGap}px)
            - ${this.thumbnailSize * 0.75}px
            - ${thumbGap}px
            + ${(this.thumbnailSize - this.thumbnailSize * 0.5) / 2}px
            - ${this.thumbnailSize * 0.5}px
            - ${thumbGap * 1.2}px
          )`;
        }
        const arrowRightEl = this.$el.querySelector('#arrow-right');
        if (arrowRightEl) {
          arrowRightEl.style.left = `calc(
            +50%
            + 3*(${this.thumbnailSize}px + ${thumbGap}px)
            + ${this.thumbnailSize * 0.75}px
            + ${thumbGap}px
            - ${(this.thumbnailSize - this.thumbnailSize * 0.5) / 2}px
            + ${this.thumbnailSize * 0.5}px
            - ${thumbGap}px
          )`;
        }
      });
    },
    setActiveEpisode(index) {
      if (this.isZoomed) return
      if (this.activeInterval !== null) {
        clearInterval(this.activeInterval)
        this.activeInterval = null
      }

      this.activeEpisode = index
      // const step = index > this.activeEpisode ? 1 : -1
      // if (this.activeEpisode !== index) {
      //   this.activeEpisode += step
      // }
      this.activeInterval = setInterval(() => {
        if (this.activeEpisode === index) {
          clearInterval(this.activeInterval)
          this.activeInterval = null
        }
        else {
          // this.activeEpisode += step
          this.activeEpisode = index
        }
      }, 1000)
    },
    getVideoSrc(index) {
      const { cdnBaseUrl } = useRuntimeConfig().public
      return `${cdnBaseUrl}/video/season-${this.season}/V_S${this.season}_E${index + 1}.mp4`
    },
    getFullVideoSrc() {
      const { cdnBaseUrl } = useRuntimeConfig().public
      return `${cdnBaseUrl}/video/season-1/full_mr_c.mp4`
    },
    getImageSrc(index) {
      const img = this.images[index]
      if (!img) return ''
      if (!this.isVideoMode) return img.image
      if (this.isEnded && index === this.activeEpisode && img.finalImage) {
        return img.finalImage
      }
      return img.image
    },
    handlePlayOrPause(index) {
      if (!this.isVideoMode) return

      this.isEnded = false
      const videoEl = this.getVideoElByIndex(index)
      if (!videoEl) return

      if (this.currentPlayingIndex === index) {
        if (this.isVideoPaused) {
          videoEl.muted = this.isMuted
          const playPromise = videoEl.play()
          if (playPromise && playPromise.catch) {
            playPromise.catch(err => console.warn('Video play failed', err))
          }
          this.isVideoPaused = false
        }
        else {
          videoEl.pause()
          this.isVideoPaused = true
        }
        return
      }

      if (this.currentPlayingIndex !== null) {
        const prevVideo = this.getVideoElByIndex(this.currentPlayingIndex)
        if (prevVideo) {
          prevVideo.pause()
          prevVideo.currentTime = 0
        }
      }

      this.currentPlayingIndex = index
      this.isVideoPaused = false

      videoEl.muted = this.isMuted
      const playPromise = videoEl.play()
      if (playPromise && playPromise.catch) {
        playPromise.catch(err => console.warn('Video play failed', err))
      }
    },
    handleStop(index) {
      if (!this.isVideoMode) return

      const videoEl = this.getVideoElByIndex(index)
      if (!videoEl) return

      if (this.currentPlayingIndex !== index && this.currentPlayingIndex !== null) {
        const prevVideo = this.getVideoElByIndex(this.currentPlayingIndex)
        if (prevVideo) {
          prevVideo.pause()
          prevVideo.currentTime = 0
        }
      }

      this.currentPlayingIndex = index
      this.isEnded = false

      videoEl.pause()
      videoEl.currentTime = 0
      videoEl.muted = this.isMuted

      const playPromise = videoEl.play()
      if (playPromise && playPromise.catch) {
        playPromise.catch(err => console.warn('Video replay failed', err))
      }

      this.isVideoPaused = false
    },
    handleVideoEnded(index) {
      if (!this.isVideoMode) return

      const isCurrent = index === this.activeEpisode

      this.currentPlayingIndex = null
      this.isVideoPaused = true

      if (this.isPlayAll && isCurrent) {
        this.isEnded = false

        if (this.activeEpisode < this.maxImages - 1) {
          this.activeEpisode = this.activeEpisode + 1
        }
        else {
          this.isEnded = true
        }
        return
      }

      if (isCurrent) {
        this.isEnded = true
      }
    },
    handlePlayNext() {
      if (!this.isVideoMode) return

      this.isEnded = false

      if (this.activeEpisode < this.maxImages - 1) {
        this.nextEpisode()
      } else {
        this.handleStop(this.activeEpisode)
      }
    },
    getVideoElByIndex(index) {
      const refName = `video-${index}`
      let el = this.$refs[refName]

      if (Array.isArray(el)) {
        el = el[0]
      }

      return el || null
    },
    handleToggleMute() {
      this.isMuted = !this.isMuted

      if (this.currentPlayingIndex !== null) {
        const videoEl = this.getVideoElByIndex(this.currentPlayingIndex)
        if (videoEl) {
          videoEl.muted = this.isMuted
        }
      }
    },
    autoPlayCurrent() {
      if (!this.isVideoMode) return

      const index = this.activeEpisode
      const videoEl = this.getVideoElByIndex(index)
      if (!videoEl) return

      if (this.currentPlayingIndex !== null && this.currentPlayingIndex !== index) {
        const prevVideo = this.getVideoElByIndex(this.currentPlayingIndex)
        if (prevVideo) {
          prevVideo.pause()
          prevVideo.currentTime = 0
        }
      }

      this.currentPlayingIndex = index
      this.isVideoPaused = false
      this.isEnded = false

      videoEl.muted = this.isMuted

      const playPromise = videoEl.play()
      if (playPromise && playPromise.catch) {
        playPromise.catch(err => console.warn('Video autoplay failed', err))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.carousel-view {
  height: calc(100dvh - 110px);
}
.bundle {
  display: flex;
  flex-wrap: nowrap;
  overflow: hidden;
  justify-content: flex-start;
  transition: all 1s ease;
  width: 100%;
}

.comic-full-video {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  object-fit: cover;
}

.bundle-image-block {
  position: relative;
  transition: transform 1s ease, left 1s ease, right 1s ease;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 32px;
  overflow: hidden;

  .comic-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .comic-image-overlay {
    transition: all 0.5s ease, filter 0.5s ease;
    width: 100%;
    height: 100%;
    position: absolute;
    background: rgba(0, 0, 0, 0.75);
    top: 0;
    right: 0;
    backdrop-filter: blur(32px);
    opacity: 0;
    z-index: 2;

    &.active {
      opacity: 1;
    }
  }
  .comic-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.bundle-image-block.active {
  .is-mode-changing {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    position: absolute;
    background: rgba(0, 0, 0, 0.95);
    top: 0;
    right: 0;
  }
}

.video-buttons {
  position: absolute;
  display: flex;
  gap: 8px;
  align-items: center;
  width: 88px;
}

.bundle-image-block.active {
  z-index: 5;
}

.bundle-image-block.prev-1,
.bundle-image-block.next-1 {
  z-index: 4;
}
.bundle-image-block.prev-2,
.bundle-image-block.next-2 {
  z-index: 3;
}
.bundle-image-block.prev-1 {
  left: 40%;
}
.bundle-image-block.prev-1.can-hover:hover:not(.empty-episode) {
  left: calc(40% - 2%);
  z-index: 5;
}

.bundle-image-block.prev-2 {
  left: 30%;
}

.bundle-image-block.next-1 {
  left: 60%;
}
.bundle-image-block.next-1.can-hover:hover:not(.empty-episode) {
  left: calc(60% + 2%);
}

.bundle-image-block.next-1.hovered,
.bundle-image-block.prev-1.hovered {
  // z-index: 5;
}

.active-episode-hide {
  .comic-image-overlay {
    opacity: 1;
  }
}

.bundle-image-block.next-2 {
  left: 70%;
}

.bundle-image-block.prev-1:not(.empty-episode),
.bundle-image-block.next-1:not(.empty-episode) {
  cursor: pointer;

  &:hover {
    .comic-image-overlay {
      opacity: 0;
    }
  }
}

.bundle-image-block.prev-2:not(.empty-episode),
.bundle-image-block.next-2:not(.empty-episode) {
  &:hover {
    .comic-image-overlay {
      opacity: 0.95;
    }
  }
}

.bundle-image-block.prev-1.empty-episode,
.bundle-image-block.prev-2.empty-episode,
.bundle-image-block.next-1.empty-episode,
.bundle-image-block.next-2.empty-episode {
  border: 1px solid #717171;
  border-style: dotted;
}

.bundle-image-block:not(.active):not(.prev-1):not(.prev-2):not(.next-1):not(.next-2) {
  z-index: 0;
  transform: scale(0.01);
}
.bundle-image-block.prev-3 {
  opacity: 0;
  transform: scale(0.01);
  left: -10%;
}

.bundle-image-block.next-3 {
  transform: scale(0.01);
  right: 0%;
}
.comic-footer-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 17dvh;
  // min-height: 122px;
  transition: bottom 1s ease;
}

.thumbnail-list {
  height: calc(100% - 80px);
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;

  .thumbnail {
    position: absolute;
    // bottom: 0;
    top: 130%;
    left: 50%;
    transform: translateX(-50%);
    cursor: pointer;
    transition: all 1s ease;
    height: 100%;
    // border-radius: 16px;
    // max-height: 64px;
    opacity: 0;
    z-index: 1;

    .thumbnail-image {
      width: 100%;
      height: 100%;
      filter: grayscale(100%);
      border-radius: 16px;
      transition: border-radius 0.5s ease;
      object-fit: cover;
      border: 2px solid transparent;
    }

    &:hover {
      .thumbnail-image {
        filter: none;
      }
    }
  }
  .thumbnail.empty {
    border: 1px solid #717171;
    border-style: dotted;
    border-radius: 16px;
    cursor: initial;
  }

  .thumbnail.selected {
    left: 50%;

    .thumbnail-image {
      border: 2px solid #fff;
      z-index: 2;
      filter: none;
    }
  }
  .thumbnail.next-4 {
    border-radius: 8px;
    .thumbnail-image {
      border-radius: 8px;
    }
  }

  .thumbnail.prev-4 {
    border-radius: 8px;
    .thumbnail-image {
      border-radius: 8px;
    }
  }
}
.thumbnail.selected,
.thumbnail.prev-1,
.thumbnail.prev-2,
.thumbnail.prev-3,
.thumbnail.prev-4,
.thumbnail.next-1,
.thumbnail.next-2,
.thumbnail.next-3,
.thumbnail.next-4 {
  opacity: 1;
  transform: translateX(-50%) scale(1);
}

.thumbnail.next-5 {
  transform: scale(0.01);
}

.episode-counter {
  color: #fff;
  font-size: 24px;
  text-align: center;
  line-height: 1;
  margin-top: 20px;
  margin-bottom: 20px;
}

.carousel-view.full-screen {
  height: calc(100dvh - 32px);

  .comic-footer-wrap {
    bottom: -82px;
  }
}
.thumb-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.border-button {
  border: 2px solid $yellow-100;
  background: transparent;
  color: $yellow-100;
  border-radius: 30px;
  padding: 12px 30px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  outline: none;
  white-space: nowrap;
  position: absolute;
  width: 164px;
  left: calc(50% - 82px);
  height: 48px;
  top: calc(50% - 24px);
  transition: all 1s ease;
  transform: scale(0.01);

  &:hover {
    background: #272726;
  }
}
.border-button.prev-1 {
  transform: translateX(-162px) scale(1);
}
.border-button.prev-2 {
  transform: translateX(-200px) scale(1);
}
.border-button.prev-3 {
  transform: translateX(-238px) scale(0.5);
}
.border-button.prev-4 {
  transform: translateX(-270px) scale(0.2);
}
.border-button.prev-5 {
  transform: translateX(-300px) scale(0.01);
}
.border-button.next-1 {
  transform: translateX(162px) scale(1);
}
.border-button.next-2 {
  transform: translateX(200px) scale(1);
}
.border-button.next-3 {
  transform: translateX(238px) scale(0.5);
}
.border-button.next-4 {
  transform: translateX(270px) scale(0.2);
}
.border-button.next-5 {
  transform: translateX(300px) scale(0.01);
}

@media only screen and (max-width: $md) {
  .bundle-image-block.prev-1 {
    left: 37%;
  }
  .bundle-image-block.prev-1.can-hover:hover:not(.empty-episode) {
    left: calc(37% - 2%);
  }
  .bundle-image-block.prev-2 {
    left: 24%;
  }
  .bundle-image-block.next-1 {
    left: 63%;
  }
  .bundle-image-block.next-1.can-hover:hover:not(.empty-episode) {
    left: calc(63% + 2%);
  }
  .bundle-image-block.next-2 {
    left: 76%;
  }
}
@media only screen and (max-width: $xs) {
  .bundle-image-block:not(.active):not(.prev-1):not(.prev-2):not(.next-1):not(.next-2) {
    display: none !important;
  }
  .comic-footer-wrap {
    height: 20dvh;
  }
  .comics-footer {
    width: 95vw;
    overflow-y: hidden;
  }

  .thumbnail-list {
    .thumbnail.next-2,
    .thumbnail.prev-2 {
      border-radius: 8px;

      .thumbnail-image {
        border-radius: 8px;
      }
    }
  }

  .thumbnail.selected,
  .thumbnail.prev-1,
  .thumbnail.prev-2,
  .thumbnail.next-1,
  .thumbnail.next-2 {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }

  .thumbnail.prev-3,
  .thumbnail.prev-4,
  .thumbnail.next-3,
  .thumbnail.next-4,
  .thumbnail.next-5 {
    transform: scale(0.01);
    left: 100%;
    top: 50%;
  }

  .carousel-view {
    height: calc(100dvh - 150px);
  }

  .carousel-view.full-screen {
    .comic-footer-wrap {
      bottom: -25px;
    }
  }
  .border-button {
    width: 100px;
    padding: 12px 20px;
  }
  .border-button.prev-1 {
    transform: translateX(-80px) scale(1);
  }
  .border-button.prev-2 {
    transform: translateX(-110px) scale(0.7);
  }
  .border-button.prev-3 {
    transform: translateX(-140px) scale(0.01);
  }
  .border-button.prev-4 {
    transform: translateX(-140px) scale(0.01);
  }
  .border-button.prev-5 {
    transform: translateX(-140px) scale(0.01);
  }

  .border-button.next-1 {
    transform: translateX(142px) scale(1);
  }
  .border-button.next-2 {
    transform: translateX(170px) scale(0.7);
  }
  .border-button.next-3 {
    transform: translateX(200px) scale(0.01);
  }
  .border-button.next-4 {
    transform: translateX(200px) scale(0.01);
  }
  .border-button.next-5 {
    transform: translateX(200px) scale(0.01);
  }
}
</style>
